import { usePostsFeatured } from "@gik/api/posts/postsFeatured";
import { useLatestPosts } from "@gik/api/posts/postsLatest";
import { ArticlesCarousel } from "@gik/blog/components/ArticlesCarousel/ArticlesCarousel";
import { ArticlesGrid } from "@gik/blog/components/ArticlesGrid/ArticlesGrid";
import { BlogHomepage } from "@gik/blog/components/BlogHomepage/BlogHomepage";
import type Article from "@gik/blog/models/Article";
import WordpressConverter from "@gik/blog/utils/WordpressConverter";
import { DynamicComponent } from "@gik/core/components/DynamicComponent";
import type WordpressPost from "@gik/core/models/wordpress/WordpressPost";
import { useBemCN } from "@gik/core/utils/bemBlock";
import { ArticleTile } from "@gik/ui-legacy/gik/ArticleTile";
import { PageSection } from "@gik/ui-legacy/gik/PageSection";
import { LayoutAside } from "@gik/ui-legacy/LayoutAside";
import React from "react";

export interface IFeaturedArticlesSectionProps {
	className?: string;
}

const blockName = "featured-articles-section";

export function FeaturedArticlesSection({
	className,
}: IFeaturedArticlesSectionProps) {
	const bem = useBemCN(blockName);
	
	const carouselRequestKey = `featured-articles-section-carousel`;
	const latestRequestKey = `featured-articles-section-latest`;

	return (
		<PageSection {...bem(null, null, className)}>
			<LayoutAside gutter={"0"}>
				<main>
					<DynamicComponent<WordpressPost[], Article[]>
						height={400}
						requestKey={carouselRequestKey}
						useNetworkStore={true}
						fetch={function useFetch() {
							return usePostsFeatured(
								{
									slug: "learn-page-primary-carousel",
									perPage: 5,
									fields: BlogHomepage.requiredWPFields.carouselArticles,
								},
								{
									requestKey: carouselRequestKey,
								}
							);
						}}
						transform={(posts) =>
							posts
								.map((post) => WordpressConverter.convertPost(post))
								.filter(WordpressConverter.filterOutPreviewContent)
						}
						loadingComponent={() => <ArticleTile skeleton />}
					>
						{({ transformed: articles }) => (
							<ArticlesCarousel articles={articles} />
						)}
					</DynamicComponent>
				</main>
				<aside>
					<DynamicComponent<WordpressPost[], Article[]>
						height={400}
						requestKey={latestRequestKey}
						useNetworkStore={true}
						fetch={function useFetch() {
							return useLatestPosts(
								{
									fields: BlogHomepage.requiredWPFields.latestArticles,
								},
								{
									requestKey: latestRequestKey,
								}
							);
						}}
						transform={(posts) =>
							posts
								.map((post) => WordpressConverter.convertPost(post))
								.filter(WordpressConverter.filterOutPreviewContent)
						}
						loadingComponent={() => (
							<ArticlesGrid
								articles={Array.from(new Array(3))}
								title={"Latest stories"}
								layout={"row"}
								tileLayout={"row"}
								skeleton
							/>
						)}
					>
						{({ transformed: articles }) => (
							<ArticlesGrid
								articles={articles}
								title={"Latest stories"}
								layout={"row"}
								tileLayout={"row"}
							/>
						)}
					</DynamicComponent>
				</aside>
			</LayoutAside>
		</PageSection>
	);
}
